import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "motion/react";

const PLANS = [
  {
    name: "Pay Per Use",
    desc: "Perfect for occasional users",
    monthly: null,
    yearly: null,
    perUse: true,
    features: ["Capital Gain Report — Rs 99/report", "Bank Statement to Excel — Rs 49/file", "No subscription required", "Download instantly", "Email report option"],
    cta: "Use Now",
    highlight: false,
    slideFrom: { x: -40, opacity: 0 },
  },
  {
    name: "CA Plan",
    desc: "For individual Chartered Accountants",
    monthly: 999,
    yearly: 7999,
    perUse: false,
    features: ["Unlimited Capital Gain reports", "Unlimited Bank Statement conversions", "Priority processing", "All free tools included", "Email support"],
    cta: "Get CA Plan",
    highlight: true,
    slideFrom: { y: 40, opacity: 0 },
  },
  {
    name: "CA Firm Plan",
    desc: "For CA firms with multiple clients",
    monthly: 2499,
    yearly: 19999,
    perUse: false,
    features: ["Everything in CA Plan", "Multiple team member logins", "White-label reports with firm logo", "Client management dashboard", "Priority phone support"],
    cta: "Get Firm Plan",
    highlight: false,
    slideFrom: { x: 40, opacity: 0 },
  },
];

export default function Pricing() {
  const { t } = useTranslation();
  const [yearly, setYearly] = useState(false);

  return (
    <div>
      <div className="page-header-dark">
        <div className="ph-dotgrid" />
        <div className="ph-glow" />
        <div className="ph-inner">
          <div className="ph-breadcrumb">
            <a href="/">Home</a>
            <span>›</span>
            <span style={{ color: "#C9A84C" }}>Pricing</span>
          </div>
          <h1>Simple, Transparent Pricing</h1>
          <p className="ph-subtitle">Pay only for what you use, or save more with a subscription</p>
        </div>
      </div>
      <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "40px 24px" }}>
      <div style={{ textAlign: "center", marginBottom: "32px" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: "10px", background: "white", border: "1.5px solid #EAE6E0", borderRadius: "10px", padding: "6px 16px" }}>
          <span style={{ fontSize: "14px", color: yearly ? "#8C8C8C" : "#2C2C2C", fontWeight: yearly ? 400 : 600 }}>{t("pricing.monthly")}</span>
          <button onClick={() => setYearly(!yearly)} style={{ width: "44px", height: "24px", borderRadius: "12px", background: yearly ? "#C9A84C" : "#D8D4CE", border: "none", cursor: "pointer", position: "relative", transition: "background 0.2s" }}>
            <div style={{ width: "18px", height: "18px", borderRadius: "50%", background: "white", position: "absolute", top: "3px", left: yearly ? "23px" : "3px", transition: "left 0.2s" }}></div>
          </button>
          <span style={{ fontSize: "14px", color: yearly ? "#2C2C2C" : "#8C8C8C", fontWeight: yearly ? 600 : 400 }}>
            {t("pricing.yearly")} <span style={{ background: "#E2F0D9", color: "#375623", fontSize: "11px", padding: "2px 6px", borderRadius: "4px", fontWeight: 700, marginLeft: "4px" }}>{t("pricing.save")}</span>
          </span>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px" }}>
        {PLANS.map((plan, idx) => (
          <motion.div
            key={idx}
            initial={plan.slideFrom}
            animate={{ x: 0, y: 0, opacity: 1 }}
            transition={{ duration: 0.45, delay: idx * 0.08, ease: "easeOut" }}
            style={{ background: "white", borderRadius: "16px", border: plan.highlight ? "2px solid #C9A84C" : "1.5px solid #EAE6E0", padding: "28px 24px", position: "relative", transition: "box-shadow 0.2s" }}
          >
            {plan.highlight && (
              <div
                className="badge-pulse"
                style={{ position: "absolute", top: "-12px", left: "50%", transform: "translateX(-50%)", background: "#C9A84C", color: "#2C2C2C", fontSize: "11px", fontWeight: 700, padding: "4px 14px", borderRadius: "10px", whiteSpace: "nowrap" }}
              >
                {t("pricing.mostPopular")}
              </div>
            )}
            <h3 style={{ fontSize: "18px", fontWeight: 700, color: "#2C2C2C", marginBottom: "4px" }}>{plan.name}</h3>
            <p style={{ fontSize: "13px", color: "#8C8C8C", marginBottom: "20px" }}>{plan.desc}</p>

            {plan.perUse ? (
              <div style={{ marginBottom: "24px" }}>
                <div style={{ fontSize: "28px", fontWeight: 800, color: "#C9A84C" }}>{t("pricing.payAsYouGo")}</div>
                <div style={{ fontSize: "13px", color: "#8C8C8C", marginTop: "4px" }}>{t("pricing.noCommitment")}</div>
              </div>
            ) : (
              <div style={{ marginBottom: "24px" }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
                  <span style={{ fontSize: "14px", color: "#8C8C8C", fontWeight: 500 }}>Rs</span>
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={yearly ? "yearly" : "monthly"}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                      style={{ fontSize: "36px", fontWeight: 800, color: "#2C2C2C", display: "inline-block" }}
                    >
                      {yearly ? Math.round((plan.yearly || 0) / 12) : plan.monthly}
                    </motion.span>
                  </AnimatePresence>
                  <span style={{ fontSize: "13px", color: "#8C8C8C" }}>{t("pricing.perMonth")}</span>
                </div>
                {yearly && <div style={{ fontSize: "12px", color: "#375623", marginTop: "4px", fontWeight: 600 }}>{t("pricing.billedYearly")} Rs {plan.yearly}/year</div>}
              </div>
            )}

            <ul style={{ listStyle: "none", padding: 0, marginBottom: "24px", display: "flex", flexDirection: "column", gap: "10px" }}>
              {plan.features.map((f, fi) => (
                <li key={fi} style={{ display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "13px", color: "#4C4C4C" }}>
                  <span style={{ color: "#C9A84C", fontWeight: 700, flexShrink: 0, marginTop: "1px" }}>✓</span>
                  {f}
                </li>
              ))}
            </ul>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{ width: "100%", padding: "12px", background: plan.highlight ? "#C9A84C" : "transparent", color: plan.highlight ? "#2C2C2C" : "#C9A84C", border: "2px solid #C9A84C", borderRadius: "8px", fontSize: "14px", fontWeight: 700, cursor: "pointer", transition: "all 0.2s" }}
              onMouseEnter={e => { if (!plan.highlight) { e.currentTarget.style.background = "#C9A84C"; e.currentTarget.style.color = "#2C2C2C"; }}}
              onMouseLeave={e => { if (!plan.highlight) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#C9A84C"; }}}
            >
              {plan.cta}
            </motion.button>
          </motion.div>
        ))}
      </div>

      <div style={{ textAlign: "center", marginTop: "36px", padding: "24px", background: "white", borderRadius: "12px", border: "1px solid #EAE6E0" }}>
        <p style={{ fontSize: "14px", color: "#5C5C5C" }}>
          {t("pricing.trial")} {t("pricing.questions")} <a href="/contact" style={{ color: "#C9A84C", fontWeight: 600 }}>{t("pricing.contact")}</a>
        </p>
      </div>
      </div>
    </div>
  );
}
